import { R } from '@utils/currency'
import type { Comment, Feedback, Month, PeriodKey, Trend } from './types'

type Period = {
  key: PeriodKey
  months: number | null
  // The period selector's button.
  label: string
  // The panel tables' rows: the server's period counts, named in full.
  name: string
  // "No new ratings in the last 3 months"; since the start isn't recent.
  recent: string | null
}

// Every period's words live here, so adding or renaming one is one edit.
export const PERIODS: Period[] = [
  { key: 'last_month', months: 1, label: 'Last month', name: 'Last month', recent: 'in the last month' },
  { key: 'three_months', months: 3, label: '3 months', name: 'Last 3 months', recent: 'in the last 3 months' },
  { key: 'twelve_months', months: 12, label: '12 months', name: 'Last 12 months', recent: 'in the last 12 months' },
  { key: 'since_start', months: null, label: 'Since start', name: 'Since the start', recent: null },
]

export const PERIOD_NAMES = Object.fromEntries(PERIODS.map(({ key, name }) => [key, name])) as Record<PeriodKey, string>

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

// "R42 000": thousands grouped with a space, no space after the R.
export const formatRands = (value: number): string => R(value, { symbol: 'R' }).format()

// 10.6 → "10.6%", 69 → "69%". Shares and ratios arrive as 0-100.
export const formatPercent = (value: number, digits = 1): string => {
  const rounded = Number(value.toFixed(digits))
  return `${rounded}%`
}

const parseMonth = (month: Month): { year: number; index: number } => {
  const [year, monthNumber] = month.split('-').map(Number)
  return { year, index: monthNumber - 1 }
}

// "2026-02" → "Feb"
export const shortMonth = (month: Month): string => MONTH_NAMES[parseMonth(month).index]

// "2026-02" → "Feb 2026"
export const longMonth = (month: Month): string => {
  const { year, index } = parseMonth(month)
  return `${MONTH_NAMES[index]} ${year}`
}

// "2026-09" → "09/2026"
export const slashMonth = (month: Month): string => {
  const { year, index } = parseMonth(month)
  return `${String(index + 1).padStart(2, '0')}/${year}`
}

// "2026-02", "2026-09" → "Feb to Sep 2026"; across years "Nov 2025 to Feb 2026".
export const monthRange = (from: Month, to: Month): string => {
  if (from === to) return longMonth(to)
  if (parseMonth(from).year === parseMonth(to).year) return `${shortMonth(from)} to ${longMonth(to)}`
  return `${longMonth(from)} to ${longMonth(to)}`
}

// "2026-09" minus 3 → "2026-06"
export const addMonths = (month: Month, offset: number): Month => {
  const { year, index } = parseMonth(month)
  const total = year * 12 + index + offset
  const newYear = Math.floor(total / 12)
  const newMonth = (total % 12) + 1
  return `${newYear}-${String(newMonth).padStart(2, '0')}`
}

// The month a period's change is measured from: N months before the latest
// month on the chart, or the first month for "since start". Never earlier
// than the first month there is.
export const baselineMonth = (series: { month: Month }[], period: PeriodKey): Month | null => {
  if (series.length === 0) return null

  const first = series[0].month
  const latest = series[series.length - 1].month
  const months = PERIODS.find((p) => p.key === period)?.months ?? null
  if (months === null) return first

  const candidate = addMonths(latest, -months)
  return candidate < first ? first : candidate
}

// The part of a monthly series a period shows: from its baseline month to the latest.
export const sliceSeries = <T extends { month: Month }>(series: T[], period: PeriodKey): T[] => {
  const from = baselineMonth(series, period)
  if (from === null) return []

  return series.filter((point) => point.month >= from)
}

export const prefersReducedMotion = (): boolean => {
  if (typeof window === 'undefined' || !window.matchMedia) return false
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

// Smooth unless the viewer asked for reduced motion. Sections carry a
// scroll-margin-top that keeps their heading clear of the sticky bar.
export const scrollToSection = (id: string): void => {
  document.getElementById(id)?.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'start' })
}

// en-US for the weekday ("Mon"); month names are MONTH_NAMES, as everywhere.
const SAST_PARTS = new Intl.DateTimeFormat('en-US', {
  weekday: 'short',
  day: 'numeric',
  month: 'numeric',
  year: 'numeric',
  timeZone: 'Africa/Johannesburg',
})

type SastDate = { year: number; month: number; day: number; weekday: string }

// A time's date on the South African calendar, whatever offset it was sent
// with. Every date helper reads it, so they agree on a missing or unreadable
// time: null.
const sastDate = (at: string | null): SastDate | null => {
  if (!at) return null

  const date = new Date(at)
  if (Number.isNaN(date.getTime())) return null

  const parts: Record<string, string> = {}
  SAST_PARTS.formatToParts(date).forEach(({ type, value }) => {
    parts[type] = value
  })
  return { year: Number(parts.year), month: Number(parts.month), day: Number(parts.day), weekday: parts.weekday }
}

// "2026-09-21T01:30:00+02:00" → "21 Sep 2026"
export const formatDate = (at: string | null): string | null => {
  const date = sastDate(at)
  return date ? `${date.day} ${MONTH_NAMES[date.month - 1]} ${date.year}` : null
}

// "2026-09-21T01:30:00+02:00" → "2026-09"
export const monthOf = (at: string | null): Month | null => {
  const date = sastDate(at)
  return date ? `${date.year}-${String(date.month).padStart(2, '0')}` : null
}

// 182 of 240 → 76. null when there is nobody to take a share of.
export const shareOf = (part: number, whole: number): number | null => (whole > 0 ? Math.round((part / whole) * 100) : null)

// 1 → "1 person has", 37 → "37 people have": a count before its verb.
export const peopleHave = (count: number): string => `${count} ${count === 1 ? 'person has' : 'people have'}`

type Point = { month: Month; value: number | null }

// The first and latest months that have a value.
export const firstValue = <T extends Point>(series: T[]): (T & { value: number }) | null =>
  series.find((point): point is T & { value: number } => point.value !== null) ?? null
export const lastValue = <T extends Point>(series: T[]): (T & { value: number }) | null => firstValue([...series].reverse())

// A round top for a percentage axis, above the highest point or guide, that
// halves into round ticks (20 → 0, 10, 20). Past 100 it steps in fifties.
const AXIS_TOPS = [5, 10, 20, 30, 40, 50, 60, 80, 100]
export const axisMax = (highest: number): number =>
  AXIS_TOPS.find((top) => top > highest) ?? Math.ceil((highest + 1) / 50) * 50

// Graded as the monthly report does: debt and spending are within their guide
// at or under it, investing only over it.
export type GuideDirection = 'at_or_under' | 'over'
export type GuideStatus = 'on_track' | 'watch' | 'needs_attention'

// Within the guide is on track. Just outside it, within a fifth of the guide
// (debt guide 10%: up to 12%; investing guide 15%: from 12%), is watch.
// Further out needs attention.
export const guideStatus = (value: number, guide: number, direction: GuideDirection): GuideStatus => {
  const within = direction === 'at_or_under' ? value <= guide : value > guide
  if (within) return 'on_track'

  const near = direction === 'at_or_under' ? value <= guide * 1.2 : value >= guide * 0.8
  return near ? 'watch' : 'needs_attention'
}

// Moved towards the right side of the guide: down for debt and spending, up for investing.
export const isImproving = (first: number, latest: number, direction: GuideDirection): boolean =>
  direction === 'at_or_under' ? latest < first : latest > first

// Band lower bounds [0, 35, 45, 65, 75] → ["<35%", "35–45%", "45–65%", "65–75%", "≥75%"]
export const bandRanges = (bands: number[]): string[] =>
  bands.map((lower, index) => {
    if (index === 0) return `<${bands[1]}%`
    if (index === bands.length - 1) return `≥${lower}%`
    return `${lower}–${bands[index + 1]}%`
  })

// A comment ready to read: prepared, with its text. The rest are being
// prepared or hidden under the minimum number of reviews.
export const isReadyComment = (comment: Comment): boolean => comment.state === 'ready' && comment.text !== null

export const hasReadyComments = (feedback: Feedback): boolean => feedback.comments.some(isReadyComment)

// "since Feb 2026", or "since the start" before the first capture is known.
export const sincePhrase = (firstMonth: Month | null): string => (firstMonth ? `since ${longMonth(firstMonth)}` : 'since the start')

// "in the last 3 months"; null for since start, which isn't recent.
export const recentPhrase = (period: PeriodKey): string | null => PERIODS.find((p) => p.key === period)?.recent ?? null

// 1 → "1 workshop", 9 → "9 workshops"; plural(2, 'person', 'people') → "2 people".
export const plural = (count: number, noun: string, nouns = `${noun}s`): string => `${count} ${count === 1 ? noun : nouns}`

// Whether a trend card can say its measure got better or worse. Judged on the
// points the chart shows (the period's slice), first shown month to latest:
// better needs the average to move towards the guide and the share within the
// guide to rise; worse is the mirror. Anything mixed or flat, or too little
// data, is steady and gets the neutral title.
export type TrendVerdict = 'improving' | 'worsening' | 'steady'

export const trendVerdict = (points: Trend['series'], direction: GuideDirection): TrendVerdict => {
  const first = firstValue(points)
  const latest = lastValue(points)
  if (!first || !latest || first.month === latest.month) return 'steady'

  const firstShare = first.share_within_guide
  const latestShare = latest.share_within_guide
  if (firstShare === null || latestShare === null) return 'steady'

  if (isImproving(first.value, latest.value, direction) && latestShare > firstShare) return 'improving'
  if (isImproving(latest.value, first.value, direction) && latestShare < firstShare) return 'worsening'
  return 'steady'
}

export const FEEDBACK_CHANNELS = ['coaching', 'workshop'] as const
export type FeedbackChannel = typeof FEEDBACK_CHANNELS[number]

// "Coaching has no reviews yet", "4 workshop reviews", "Workshops have".
export const CHANNEL_WORDS: Record<FeedbackChannel, { name: string; noun: string; verb: string }> = {
  coaching: { name: 'Coaching', noun: 'coaching', verb: 'has' },
  workshop: { name: 'Workshops', noun: 'workshop', verb: 'have' },
}

// Why a channel has no rating: "Coaching has no reviews yet", or
// "Fewer than 5 people have reviewed workshops" (however many reviews).
export const unratedText = (channel: FeedbackChannel, reviews: number, minimum: number): string => {
  const { name, verb } = CHANNEL_WORDS[channel]
  if (reviews === 0) return `${name} ${verb} no reviews yet`
  return `Fewer than ${minimum} people have reviewed ${name.toLowerCase()}`
}

// The headline rating as the server builds it: only channels with at least
// minimum_reviews different people count. "none" before anyone has reviewed,
// "hidden" while no channel qualifies.
export type RatingSummary =
  | { state: 'none' }
  | { state: 'hidden'; reviews: number }
  | { state: 'rated'; average: number; channels: FeedbackChannel[]; reviews: number; top: FeedbackChannel }

export const ratingSummary = (feedback: Feedback): RatingSummary => {
  if (feedback.reviews === 0) return { state: 'none' }

  const channels = FEEDBACK_CHANNELS.filter((channel) => {
    const { average, people } = feedback.channels[channel]
    return average !== null && people >= feedback.minimum_reviews
  })
  if (feedback.average === null || channels.length === 0) return { state: 'hidden', reviews: feedback.reviews }

  const averageOf = (channel: FeedbackChannel) => feedback.channels[channel].average ?? 0
  const top = channels.reduce((best, channel) => (averageOf(channel) > averageOf(best) ? channel : best))
  const reviews = channels.reduce((sum, channel) => sum + feedback.channels[channel].reviews, 0)

  return { state: 'rated', average: feedback.average, channels, reviews, top }
}

// Periods count the rated channels only, so with one rated it is named.
const ratedKind = (channels: FeedbackChannel[]): string => (channels.length === 1 ? `${CHANNEL_WORDS[channels[0]].noun} ` : '')

// "No new coaching ratings in the last month", or "3 coaching reviews in the
// last 3 months": a recent period without a rating of its own. null when there
// is no rating, or the period has its own.
export const periodRatingLead = (feedback: Feedback, period: PeriodKey): string | null => {
  const summary = ratingSummary(feedback)
  const { reviews, average } = feedback.periods[period]
  if (summary.state !== 'rated' || period === 'since_start' || average !== null) return null

  const kind = ratedKind(summary.channels)
  const when = recentPhrase(period)
  return reviews === 0 ? `No new ${kind}ratings ${when}` : `${plural(reviews, `${kind}review`)} ${when}`
}

// "No new coaching ratings in the last month · 4.6 from 40 coaching reviews
// since the start": the lead against the whole programme's rating.
export const periodRatingNote = (feedback: Feedback, period: PeriodKey): string | null => {
  const lead = periodRatingLead(feedback, period)
  const summary = ratingSummary(feedback)
  if (lead === null || summary.state !== 'rated') return null

  const hidden = feedback.periods[period].reviews > 0 ? ': hidden for now' : ''
  return `${lead}${hidden} · ${summary.average.toFixed(1)} from ${summary.reviews} ${ratedKind(summary.channels)}reviews since the start`
}

// "1 person", "98 people". Company totals show as they are, however small:
// only breakdowns that could point to someone are withheld (on the server).
export const formatPeople = (count: number): string => plural(count, 'person', 'people')

// A value placed on a 0 to 100 scale: 112 → 100, -4 → 0.
export const clampPercent = (value: number): number => Math.max(0, Math.min(value, 100))

// "2026-09-21T01:30:00+02:00" → "Mon 21 Sep"
export const formatUpdated = (at: string | null): string | null => {
  const date = sastDate(at)
  return date ? `${date.weekday} ${date.day} ${MONTH_NAMES[date.month - 1]}` : null
}
